import "./index.css";

import React from "react";
import DialogDataComponent, {executeComponentCallback} from 'Core/components/DialogDataComponent';
import PropTypes from "prop-types";
import {connect} from 'react-redux';
import {ExpertReportDataObject, ExpertReportSectionDataObject} from 'DataObjects/expertReport';
import DataValueValidation, {ValidationRuleObject} from "Core/validation";
import FormWrapper, {FormField} from "Core/components/advanced/FormWrapper";
import {getArray, getString, isset} from 'Core/helpers/data';
import * as actions from "./actions";
import {getGlobalActions} from 'Core/helpers/redux';
import DateInput from 'Core/components/input/DateInput';
import TextInput from "Core/components/input/TextInput";
import {loadPatientTherapyAction} from "Components/advanced/PatientRecord/components/Therapy/actions";
import {loadPatientRecordAction} from "Components/advanced/PatientRecord/actions";
import {FORM_FIELD_LABEL_POSITION} from "Core/components/advanced/FormWrapper/FormField";
import Label from "Core/components/display/Label";
import {routerPath as expertReportTypesPageRouterPath} from "Pages/apps/default/expertReportTypes";
import SelectInput from "Core/components/input/SelectInput";
import {cloneDeep, find, sortBy} from 'lodash';
import {SELECT_INPUT_TOOLBAR_POSITION} from "Core/components/input/SelectInput/const";
import {redirectToPath} from "Core/helpers/url";
import {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from "Core/components/display/Button";
import ExpertReportEditor from 'Components/advanced/ExpertReportEditor';
import {ExpertReportTypesItemDataObject} from 'Pages/apps/default/expertReportTypes/dataObjects';
import {isMacintosh} from 'Core/helpers/system';
import {
	icon_font_close_symbol,
	icon_font_create_symbol,
	icon_font_delete_symbol,
	icon_font_restore_symbol, icon_font_save_symbol
} from 'Config/app';

/**
 * Get all actions used by this component
 * @type {Object}
 */
const allActions = getGlobalActions({...actions, loadPatientTherapyAction, loadPatientRecordAction});

class ExpertReportDialog extends DialogDataComponent {
	constructor(props) {
		super(props, {
			data:
				props.isNew ?
					new ExpertReportDataObject(
						null,
						props.patientId,
						props.therapyId,
						new Date(),
					)
					:
					null,

			/**
			 * List of all expert report types
			 * @type {?ExpertReportTypesItemDataObject|undefined}
			 */
			types: null,
			/**
			 * Expert report type sections that can be added to the report
			 * @type {ExpertReportTypeSectionDataObject[]}
			 */
			newSections: [],
		}, {
			translationPath: 'ExpertReportDialog',
			domPrefix: 'expert-report-dialog',
			disableLoad: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onClose', 'onChange', 'onSave', 'onDelete', 'onEnterKey'],
		});
		
		// Refs
		this.editorRef = null;

		// Action methods
		this.addSection = this.addSection.bind(this);
		
		// Data change handling methods 
		this.handleTypeChange = this.handleTypeChange.bind(this);
	}

	/** @inheritDoc */
	async asyncComponentDidMount(override = false) {
		await super.asyncComponentDidMount(override);

		const {isNew, id, therapyId, fetchExpertReportAction, fetchExpertReportTypesAction} = this.props;

		// Load expert report types
		await this.executeAbortableActionMount(fetchExpertReportTypesAction, therapyId)
			.then(types => this.setState({types}));
		
		// Load data if on the "edit" dialog and ID is specified
		if (!isNew && !!id) {
			await this.executeAbortableActionMount(fetchExpertReportAction, id)
				// Load expert report into the local component's state
				.then(data => {
					const type = find(this.state.types, {id: data.typeId});
					/** @type {ExpertReportSectionDataObject[]} */
					const dataSections = getArray(data, 'sections');
					/** @type {ExpertReportTypeSectionDataObject[]} */
					const typeSections = getArray(type, 'sections');
					
					// Add new sections to the list of sections that can be added to the report
					this.setState({newSections: typeSections.filter(s => !find(dataSections, {baseId: s.id}))});
					
					let dataToSet = cloneDeep(data);
					sortBy(dataToSet, ['ordinal']);
					
					return this.setData(dataToSet).then(() => this.originalData = cloneDeep(dataToSet));
				});
		} else {
			this.originalData = this.getData();
		}
	}
	
	/**
	 * Add a new section type as a section to the report and remove it from the list of sections that can be added.
	 * 
	 * @param {ExpertReportTypeSectionDataObject} section
	 * @return {Promise<Object>}
	 */
	addSection(section) {
		return this.setState(prevState => ({
			...prevState,
			data: (
				!prevState.data ?
					prevState.data
					:
					{
						...prevState.data, 
						sections: (
							!prevState.data.sections ?
								[new ExpertReportSectionDataObject(null, section.id, section.name, '', section.ordinal)] :
								[
									...prevState.data.sections,
									new ExpertReportSectionDataObject(null, section.id, section.name, '', section.ordinal)
								]
						)
					}
			),
			newSections: getArray(prevState, 'newSections').filter(s => s.GUIID !== section.GUIID),
		}));
	}
	
	/**
	 * Handle expert report type change 
	 * @param {?string} selectedOption - Selected expert report type ID.
	 * @param {ExpertReportTypesItemDataObject} selectedValue - Selected expert report type object.
	 */
	handleTypeChange(selectedOption, selectedValue) {
		// const {openDialogAction, closeDialogAction} = this.props;
		
		// If selection is cleared, do not update the report content
		if (selectedValue === null) {
			this.handleValueChange('typeId', selectedOption).then();
			return;
		}
		// Update report content
		// const dialogGUIID = openDialogAction('', ConfirmDialog, {
		// 	message: this.t('typeChangeConfirmationMessage'),
		// 	supportHtml: true,
		// 	onYes: () => {
				this.handleValueChange('typeId', selectedOption)
					.then(() => this.setState(prevState => ({
						...prevState,
						data: (
							!prevState.data ?
								prevState.data 
								:
								{
									...prevState.data, 
									content: getString(selectedValue, 'content'),
									sections: getArray(selectedValue, 'sections')
										.map(s => new ExpertReportSectionDataObject(null, s.id, s.name, s.defaultText, s.ordinal))
								}
						)
					})))
					.then(() => {
						if (this.editorRef) this.editorRef.setState({editableSectionGUIID: null});
					})
					.then(() => this.originalData = this.getData());
		// 		closeDialogAction(dialogGUIID);
		// 	},
		// }, {
		// 	id: 'confirm-expert-report-type-change-dialog',
		// 	closeOnEscape: true,
		// 	closeOnClickOutside: true,
		// 	hideCloseBtn: true,
		// 	maxWidth: 500
		// });
		// this.setOption(
		// 	'dialogsToCloseOnUnmount',
		// 	trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		// );
	}

	/**
	 * Default component's data validation method
	 *
	 * @return {boolean} True if component's data validation passed successfully.
	 */
	validate() {
		const dataValidation = new DataValueValidation();
		/** @type {ExpertReportDataObject} */
		const dataToValidate = this.getData();

		dataValidation.addRule('date', 'required', 'date');
		dataValidation.addRule('content', new ValidationRuleObject('custom', {validate: value => {
			let passed;

			if (typeof value === 'string') passed = !!value.replace(/(<([^>]+)>)/ig, "").length;
			else passed = (typeof value !== 'undefined' && value !== null);
				
			return passed ? {passed} : {passed: false, error: 'required'};
		}}));

		// Run validation
		const validationErrors = dataValidation.run(dataToValidate);

		if (validationErrors) this.setValidationErrors('', validationErrors).then();
		else this.clearValidationErrors().then();
		return !validationErrors;
	}

	/**
	 * Save dialog method
	 * @note This method should be called when dialog's "save" button is clicked. This method does not actually save any
	 * data to the DB or anywhere else. That should be done by the parent component.
	 */
	async save() {
		const {
			isNew, patientId, therapyId, dialogGUIID, createExpertReportAction, updateExpertReportAction, 
			addSuccessMessageAction, loadPatientRecordAction, loadPatientTherapyAction
		} = this.props;
		
		const data = this.getDataToReturn();

		// Do the validation
		const isValid = this.validate();

		// If validation is successful
		if (isValid) {
			const expertReport = (
				isNew ?
					await this.executeAbortableAction(createExpertReportAction, data) :
					await this.executeAbortableAction(updateExpertReportAction, data.id, data, false)
			);
			
			if (isset(expertReport)) {
				// Show success message
				if (isNew) addSuccessMessageAction(this.t('create_success_msg'));
				else addSuccessMessageAction(this.t('update_success_msg'));

				if (isNew) {
					// Reload patient record to update global statistics
					await this.executeAbortableAction(loadPatientRecordAction, patientId);
					// Reload patient therapy to update statistics
					if (!!therapyId && therapyId !== '*') {
						await this.executeAbortableAction(loadPatientTherapyAction, therapyId, '.therapy-section');
					}
				}

				this.close();

				// Trigger component's onSave event if visit was saved successfully
				executeComponentCallback(this.props.onSave, expertReport, dialogGUIID);
			}
		}
	}

	/**
	 * Render dialog action buttons used by data dialogs
	 * @note Every dialog that is used to manipulate some data should use these action buttons on the bottom of the main
	 * render method. Standard buttons include create, save, delete, restore and close and they will be rendered
	 * depending on dialog type and available events (isNew, isRestore, onDelete, ...).
	 *
	 * @param {string|null} [createLabel] - Label used for create button. Default value will be loaded from translation file.
	 * @param {string|null} [createIcon] - Optional icon used for create button.
	 * @param {string|null} [deleteLabel] - Label used for delete button. Default value will be loaded from translation file.
	 * @param {string|null} [deleteIcon] - Optional icon used for delete button.
	 * @param {string|null} [saveLabel] - Label used for save button. Default value will be loaded from translation file.
	 * @param {string|null} [saveIcon] - Optional icon used for save button.
	 * @param {string|null} [closeLabel] - Label used for close button. Default value will be loaded from translation file.
	 * @param {string|null} [closeIcon] - Optional icon used for close button.
	 * @param {string|null} [restoreLabel] - Label used for restore button. Default value will be loaded from translation
	 * file.
	 * @param {string|null} [restoreIcon] - Optional icon used for restore button.
	 * @param {boolean} [hideClose=false] - If true, close button will not be rendered. This is useful for confirm
	 * dialogs.
	 * @return {*} Action buttons JSX to use in the main render method.
	 */
	renderDataActionButtons({
		createLabel = undefined, createIcon = undefined, deleteLabel = undefined, deleteIcon = undefined,
		saveLabel = undefined, saveIcon = undefined, closeLabel = undefined, closeIcon = undefined,
		restoreLabel = undefined, restoreIcon = undefined
	} = {}, hideClose = false) {
		const {isNew, isRestore, onDelete} = this.props;
		const autofocus = this.getOption('autoFocusActionButtons');

		let buttons = [];

		// Close/cancel button
		if (isMacintosh() && !hideClose) buttons.push({
			style: BUTTON_STYLE.DEFAULT,
			label: (closeLabel ? closeLabel : this.t('Close', 'general')),
			icon: (isset(closeIcon) && closeLabel !== null ? closeIcon : icon_font_close_symbol),
			onClick: this.close,
		});

		// Delete button
		if (onDelete && !isNew) buttons.push({
			style: BUTTON_STYLE.ERROR,
			label: (deleteLabel ? deleteLabel : this.t('Delete', 'general')),
			icon: (isset(deleteIcon) && deleteIcon !== null ? deleteIcon : icon_font_delete_symbol),
			onClick: this.delete,
		});

		// Save buttons (create, restore and save)
		if (isNew) buttons.push({
			style: BUTTON_STYLE.SUCCESS,
			label: (createLabel ? createLabel : this.t('Create', 'general')),
			icon: (typeof createIcon === 'undefined' ? icon_font_create_symbol : createIcon),
			onClick: this.save,
			disabled: !(!isNew || this.getValue('typeId') !== null),
			autofocus,
		});
		else if (isRestore) buttons.push({
			style: BUTTON_STYLE.SUCCESS,
			label: (restoreLabel ? restoreLabel : this.t('Restore', 'general')),
			icon: (isset(restoreIcon) && restoreIcon !== null ? restoreIcon : icon_font_restore_symbol),
			onClick: this.save,
			autofocus,
		});
		else buttons.push({
				style: BUTTON_STYLE.SUCCESS,
				label: (saveLabel ? saveLabel : this.t('Save', 'general')),
				icon: (isset(saveIcon) && saveIcon !== null ? saveIcon : icon_font_save_symbol),
				onClick: this.save,
				autofocus,
			});

		// Close/cancel button
		if (!isMacintosh() && !hideClose) buttons.push({
			style: BUTTON_STYLE.DEFAULT,
			label: (closeLabel ? closeLabel : this.t('Close', 'general')),
			icon: (isset(closeIcon) && closeLabel !== null ? closeIcon : icon_font_close_symbol),
			onClick: this.close,
		});

		return this.renderActionButtons(buttons);
	}

	render() {
		const {isNew, therapyId} = this.props;
		const {types, newSections} = this.state;

		/** @type {ExpertReportDataObject} */
		const data = this.getData();

		return this.renderDialog(
			this.renderTitle(isNew ? this.t(!!therapyId ? 'title_create_therapy' : 'title_create') : this.t('title')),
			(
				<FormWrapper className={`dialog-form ${this.getOption('domPrefix')}`}>
					{
						!isNew || this.getValue('typeId') !== null ?
							<>
								<div className="dialog-form-toolbar">
									<FormField
										label={isNew ? this.t('typeNewSelectedLabel') : this.t('typeLabel')}
										labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
										inputClassName="full"
										errorMessages={this.getValidationErrors('type')}
									>
										<SelectInput
											className="expert-report-dialog-type-readonly"
											menuIsOpen={false}
											isSearchable={false}
											isClearable={false}
											backspaceRemovesValue={false}
											primaryKey="id"
											options={[
												new ExpertReportTypesItemDataObject('', this.t('emptyTypePlaceholder')),
												...getArray(types)
											]}
											isLoading={types === null}
											getOptionValue={o => o.id}
											getOptionLabel={o => (
												<div className="expert-report-dialog-type-option">
													<Label element="span" elementProps={{className: 'bold'}} content={o.name} />
													{!!o.templateFileName ?
														<Label element="small" content={` - ${o.templateFileName}`} />
														: null
													}
												</div>
											)}
											value={this.getValue('typeId')}
											toolbarButtons={[
												{
													className: 'expert-report-type-link',
													position: SELECT_INPUT_TOOLBAR_POSITION.LEFT,
													icon: 'external-link',
													iconProps: {symbolPrefix: 'icofont-'},
													tooltip: this.t('typeLinkTooltip'),
													displayType: BUTTON_DISPLAY_TYPE.NONE,
													displayStyle: BUTTON_STYLE.SUBTLE,
													onClick: () => redirectToPath(
														`${expertReportTypesPageRouterPath}/item/${this.getValue('typeId')}`,
														true
													),
													hide: !this.getValue('typeId'),
												}
											]}
										/>
									</FormField>
									<div className="date-and-title">
										<FormField
											required={true}
											className="date-field"
											label={this.t('dateLabel')}
											labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
											errorMessages={this.getValidationErrors('date')}
										>
											<DateInput
												preventTab={true}
												value={data?.date || null}
												onChange={v => this.handleValueChange('date', v)}
											/>
										</FormField>

										<FormField
											className="title-field"
											inputClassName="full"
											label={this.t('titleLabel')}
											labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
											errorMessages={this.getValidationErrors('title')}
										>
											<TextInput
												name="title"
												value={data?.title}
												onChange={this.handleInputChange}
											/>
										</FormField>
									</div>
								</div>

								<FormField
									required={true}
									className="multiline-form-field content-field"
									inputClassName="full"
									label={this.t('contentLabel')}
									labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
									errorMessages={this.getValidationErrors('content')}
								>
									{newSections.length > 0 ?
										<div className="expert-report-dialog-new-sections notice">
											<Label
												element="span"
												elementProps={{className: 'new-sections-title'}}
												content={this.t('newSectionsLabel')}
											/>
											<span className="section-links">
												{newSections.sort((a, b) => a.ordinal - b.ordinal).map((section, index) =>
													<span key={section.GUIID}>
														{index > 0 ? <span key={index} className="separator">, </span> : null}
														<Label
															key={section.GUIID}
															element="span"
															elementProps={{
																className: 'section-link',
																onClick: () => this.addSection(section)
															}}
															content={section.name}
														/>
													</span>
												)}
											</span>
										</div>
										: null
									}
									<ExpertReportEditor
										mainContentLabel={this.getValue('title')}
										bounds={`#${getString(this.props, 'dialogOptions.id')} .dialog-content-component`}
										data={data}
										onChange={v => {
											if (this.getOption('clearValidationErrorsOnChange') === true) {
												this.clearValidationErrors('content').then();
											}
											this.setData(v).then();
										}}
										ref={node => { this.editorRef = node; }}
									/>
								</FormField>
							</>
							:
							<div className="dialog-form-toolbar template-selector">
								<FormField
									label={this.t('typeNewLabel')}
									labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
									inputClassName="full"
									errorMessages={this.getValidationErrors('type')}
								>
									<SelectInput
										className="expert-report-dialog-type-select"
										primaryKey="id"
										isClearable={true}
										options={[
											new ExpertReportTypesItemDataObject('', this.t('emptyTypePlaceholder')),
											...getArray(types)
										]}
										isLoading={types === null}
										getOptionValue={o => o.id}
										getOptionLabel={o => (
											<div className="expert-report-dialog-type-option">
												<Label element="span" elementProps={{className: 'bold'}} content={o.name} />
												{!!o.templateFileName ?
													<Label element="small" content={` - ${o.templateFileName}`} />
													: null
												}
											</div>
										)}
										value={this.getValue('typeId')}
										onChange={this.handleTypeChange}
										toolbarButtons={[
											{
												className: 'expert-report-type-link',
												position: SELECT_INPUT_TOOLBAR_POSITION.LEFT,
												icon: 'external-link',
												iconProps: {symbolPrefix: 'icofont-'},
												tooltip: this.t('typeLinkTooltip'),
												displayType: BUTTON_DISPLAY_TYPE.NONE,
												displayStyle: BUTTON_STYLE.SUBTLE,
												onClick: () => redirectToPath(
													`${expertReportTypesPageRouterPath}/item/${this.getValue('typeId')}`,
													true
												),
												hide: !this.getValue('typeId'),
											}
										]}
									/>
								</FormField>
							</div>
						
					}
				</FormWrapper>
			),
		);
	}
}

/**
 * Define component's own props that can be passed to it by parent components
 */
ExpertReportDialog.propTypes = {
	// Flag that specifies if this dialog is for creating a new item
	// @note This prop can be dynamically changed in the popup component.
	isNew: PropTypes.bool,
	// Expert report ID used when "isNew" prop is false (edit expert report)
	id: PropTypes.string,
	// ID of the patient
	patientId: PropTypes.string.isRequired,
	// ID of the therapy
	therapyId: PropTypes.string,

	// Event triggered if expert report was saved successfully and dialog has already been closed
	// @param {ExpertReportDataObject} expert report,
	// @param {string} dialogGUIID
	onSave: PropTypes.func,
	// Event triggered when dialog close method is called
	// @note Dialog will not be closed if event callback returns false.
	// @param {string} dialogGUIID
	onClose: PropTypes.func,
};

export default connect(null, allActions)(ExpertReportDialog);